import {
  ReceiptVerificationStatus,
  verificationSortKey,
} from 'src/models/receiptVerification.models'
import { mockTransactionVerificationStatus } from 'src/models/receiptVerificationMock'
import {
  ReceiptRecoveryLifecycleState,
  TransactionListModel,
} from 'src/models/transaction.models'

type TransactionReceiptAgentData = Pick<
  TransactionListModel,
  | 'id'
  | 'receiptAgentStatus'
  | 'receiptAgentAction'
  | 'receiptMatchingStatus'
  | 'attachments'
  | 'receiptThumbnailUrl'
>

export interface ReceiptAgentTablePresentation {
  status?: ReceiptRecoveryLifecycleState
  action?: string
  thumbnailUrl?: string
  receiptVerificationStatus?: ReceiptVerificationStatus
}

export const receiptAgentTableMockFixtures: readonly ReceiptAgentTablePresentation[] =
  [
    {
      status: ReceiptRecoveryLifecycleState.NeedsReview,
      action: 'REVIEW_RECEIPT',
    },
    {
      status: ReceiptRecoveryLifecycleState.WaitingOnSpender,
      action: 'WAIT_FOR_RESPONSE',
    },
    {
      status: ReceiptRecoveryLifecycleState.ReceiptReconciled,
      action: 'NONE',
    },
  ]

const mockFixtureIndex = (transactionId: string): number =>
  [...transactionId].reduce(
    (index, character) => index + character.charCodeAt(0),
    0,
  ) % receiptAgentTableMockFixtures.length

export function getReceiptAgentTablePresentation(
  transaction: TransactionReceiptAgentData,
  mockDataEnabled: boolean,
): ReceiptAgentTablePresentation {
  const hasLiveReceiptAgentProjection =
    transaction.receiptAgentStatus !== undefined ||
    transaction.receiptAgentAction !== undefined

  if (mockDataEnabled && !hasLiveReceiptAgentProjection) {
    return {
      ...receiptAgentTableMockFixtures[mockFixtureIndex(transaction.id)],
      thumbnailUrl:
        transaction.receiptThumbnailUrl ??
        transaction.attachments?.[0]?.urls.thumb,
      receiptVerificationStatus: transaction.receiptMatchingStatus,
    }
  }

  return {
    status: transaction.receiptAgentStatus,
    action: transaction.receiptAgentAction,
    thumbnailUrl: transaction.receiptThumbnailUrl,
    receiptVerificationStatus: transaction.receiptMatchingStatus,
  }
}

export function receiptSortKeyForTransaction(
  transaction:
    | (TransactionReceiptAgentData &
        Pick<TransactionListModel, 'attachmentsCount'>)
    | undefined,
  mockDataEnabled: boolean,
): number {
  const noReceiptSortKey = verificationSortKey(undefined)
  if (transaction === undefined) return noReceiptSortKey

  const presentation = getReceiptAgentTablePresentation(
    transaction,
    mockDataEnabled,
  )
  const hasReceipt =
    (transaction.attachmentsCount ?? 0) > 0 ||
    presentation.thumbnailUrl !== undefined
  if (!hasReceipt) return noReceiptSortKey

  const status =
    presentation.receiptVerificationStatus ??
    transaction.receiptMatchingStatus ??
    mockTransactionVerificationStatus(
      transaction.id,
      transaction.attachmentsCount,
    )
  return verificationSortKey(status)
}
